import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import moment from 'moment'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import GeoContext from 'contexts/geoContext'
import TripDrawer, { type TripDrawerAnimation } from 'tripPlanner/components/GenericDrawer/TripDrawer'
import TripDrawerHeaderTitle from 'tripPlanner/components/GenericDrawer/TripDrawerHeaderTitle'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import FilterChip from 'LuxKit/Chips/FilterChip'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import HotelDetailView from './HotelListingDrawer/HotelDetailView'
import type { DrawerHotel } from './HotelListingDrawer'
import { mapOfferDetailToDrawerHotel } from './HotelListingDrawer/mapOfferDetailToDrawerHotel'
import { formatDrawerHotelInfo } from './HotelListingDrawer/formatDrawerHotelInfo'
import useOfferDetails from '../hooks/useOfferDetails'
import useOfferReviews from '../hooks/useOfferReviews'
import { useTripPlannerEventBusOptional } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import { buildOfferDrawerOpenedActivityEntry } from './buildOfferDrawerOpenedActivityEntry'
import type { TripItineraryData } from 'tripPlanner/api/types'
import type { OfferClickSearchParams } from 'tripPlanner/assistant/contexts/ChatMessageActionsContext'
import {
  isOfferBookedInItinerary,
  getOfferItineraryDayRange,
  getOfferSavedBooking,
} from 'tripPlanner/utils/chatItinerary'
import { useOfferBookingState } from 'tripPlanner/hooks/useOfferBookingState'
import AddHotelToTripButton from 'tripPlanner/components/AddToTrip/AddHotelToTripButton'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'
import { prettyFlexibleDates } from 'components/Search/SearchForm/SearchDateInput/FlexibleDateSearchField'
import {
  shouldRenderTripDrawerTitleInContent,
  useRightPanelTripDrawerPresentation,
} from 'tripPlanner/components/GenericDrawer/TripDrawerPresentationContext'
import TripDrawerAssistantBridge, { TripDrawerAssistantFooter } from './TripDrawerAssistantBridge'
import { formatDynamicDateRange } from 'lib/datetime/dateUtils'
import DrawerDetailLoadingSkeleton from './DrawerDetailLoadingSkeleton'
import { formatTripDrawerMapLabel } from './formatTripDrawerMapLabel'
import { drawerBodyEmptyStatePaddingStyles } from './drawerBodyLayout.styles'
import type { DrawerDates } from './HotelListingDrawer/useNotifyDatesChange'

const NonInteractiveChipWrapper = styled.div`
  pointer-events: none;
`

const ErrorWrapper = styled(Group)`
  ${drawerBodyEmptyStatePaddingStyles}
  text-align: center;
`

interface Props {
  offerId: string
  accommodationPropertyId?: string
  offerType?: string
  isOpen: boolean
  onClose: () => void
  onAfterClose?: () => void
  onBack?: () => void
  animation?: TripDrawerAnimation
  onShowMap?: (hotel: DrawerHotel) => void
  itineraryData?: TripItineraryData
  onSendMessage?: (message: string, options?: SendMessageOptions) => void
  searchParams?: OfferClickSearchParams
  scrollToRoomOptions?: boolean
  checkoutSource?: App.CheckoutSource
}

function ChatOfferDetailDrawer({
  offerId,
  accommodationPropertyId,
  offerType,
  isOpen,
  onClose,
  onAfterClose,
  onBack,
  animation,
  onShowMap,
  itineraryData,
  onSendMessage,
  searchParams,
  scrollToRoomOptions,
  checkoutSource,
}: Props) {
  const intl = useIntl()
  const geo = useContext(GeoContext)
  const drawerPresentation = useRightPanelTripDrawerPresentation()

  const { savedBookedRooms, detailOrderId, orderIdForLink } =
    useOfferBookingState(offerId, itineraryData)

  // A saved item's own occupancy/dates take priority over searchParams (which
  // carry whatever the caller happened to forward, e.g. checkIn/checkOut only)
  // so the price query matches what's actually rendered for this item below —
  // see initialOccupancies/checkIn/checkOut on HotelDetailView further down,
  // which already resolve the same way.
  const savedBooking = useMemo(
    () => getOfferSavedBooking(offerId, itineraryData),
    [offerId, itineraryData],
  )
  const priceQueryOccupancies = savedBooking?.occupancies ?? searchParams?.rooms

  // The room options picker (DrawerFlashHotelRoomOptions / DrawerBedbankRoomOptions /
  // DrawerAlwaysOnRoomOptions) each keep their own dates in a component-local reducer, and
  // report changes here via onDatesChange (see useNotifyDatesChange). pickerOverride is the
  // drawer's one owner for "what the picker has been changed to since it was opened" — it
  // takes precedence over the saved/search-param dates below without replacing that reactive
  // fallback chain, which still needs to pick up e.g. a savedBooking that appears mid-session.
  const [pickerOverride, setPickerOverride] = useState<DrawerDates | undefined>(undefined)
  const handleDatesChange = useCallback((dates: DrawerDates) => {
    setPickerOverride(dates)
  }, [])

  const liveCheckIn = pickerOverride?.checkIn ?? savedBooking?.checkIn ?? searchParams?.checkIn
  const liveCheckOut = pickerOverride?.checkOut ?? savedBooking?.checkOut ?? searchParams?.checkOut

  const { offerDetail, isLoading, isError, refetch } = useOfferDetails({
    offerId: isOpen ? offerId : undefined,
    region: geo.currentRegionCode,
    offerType,
    accommodationPropertyId,
    checkIn: liveCheckIn,
    checkOut: liveCheckOut,
    occupancies: priceQueryOccupancies
      ? JSON.stringify(
          priceQueryOccupancies.map((r) => ({
            adults: r.adults,
            children: r.children,
            childrenAge: r.childrenAge ?? [],
          })),
        )
      : undefined,
    flexibleMonths: searchParams?.flexibleMonths,
    flexibleNights: searchParams?.flexibleNights,
    orderId: detailOrderId,
    enabled: isOpen,
  })

  const { reviews, isLoading: reviewsLoading } = useOfferReviews({
    offerId: isOpen ? offerId : undefined,
    region: geo.currentRegionCode,
    enabled: isOpen && !!offerDetail,
  })

  const drawerHotel = useMemo(() => {
    if (!offerDetail) return undefined
    const hotel = mapOfferDetailToDrawerHotel(offerDetail)

    // Adjust display price when the currently selected dates override the default duration.
    // Keyed on liveCheckIn/liveCheckOut (not searchParams) so this stays correct once the
    // picker reports new dates — searchParams is whatever the drawer opened with and never
    // updates afterwards.
    if (liveCheckIn && liveCheckOut && hotel.price) {
      const nights = moment(liveCheckOut).diff(
        moment(liveCheckIn),
        'days',
      )
      if (nights > 0 && nights !== hotel.price.nights) {
        return { ...hotel, price: { ...hotel.price, nights } }
      }
    }

    return hotel
  }, [offerDetail, liveCheckIn, liveCheckOut])

  const eventBus = useTripPlannerEventBusOptional()
  // Fresh mount per genuine drawer open (DrawerContainer.tsx keys this component on the drawer
  // stack entry id), so a plain per-mount ref is enough -- no need to track ids across opens the
  // way DrawerContainer.tsx's own analytics effect does for its own, longer-lived mount.
  const dispatchedOfferDrawerOpenRef = useRef(false)
  useEffect(() => {
    if (dispatchedOfferDrawerOpenRef.current || !drawerHotel) return
    dispatchedOfferDrawerOpenRef.current = true
    eventBus?.dispatch({
      type: 'trip_planner_surface_activity',
      entries: [buildOfferDrawerOpenedActivityEntry(drawerHotel.name, drawerHotel.offerType ?? 'hotel')],
    })
  }, [drawerHotel, eventBus])

  const handleShowMap = useCallback(() => {
    if (drawerHotel) {
      onClose()
      onShowMap?.(drawerHotel)
    }
  }, [onClose, onShowMap, drawerHotel])

  const datesSummary = useMemo(() => {
    if (!searchParams) return undefined
    if (searchParams.checkIn && searchParams.checkOut) {
      return formatDynamicDateRange(searchParams.checkIn, searchParams.checkOut)
    }
    if (searchParams.flexibleMonths || searchParams.flexibleNights) {
      return prettyFlexibleDates(
        searchParams.flexibleMonths,
        searchParams.flexibleNights,
      )
    }
    return undefined
  }, [searchParams])

  const hasGeoLocation = !!drawerHotel?.geoLocation
  const isBooked = isOfferBookedInItinerary(offerId, itineraryData)
  const filterNights = useMemo(() => {
    if (!liveCheckIn || !liveCheckOut) return undefined
    const nights = moment(liveCheckOut).diff(
      moment(liveCheckIn),
      'days',
    )
    return nights > 0 ? nights : undefined
  }, [liveCheckIn, liveCheckOut])

  const itineraryDayLabel = useMemo(() => {
    const range = getOfferItineraryDayRange(offerId, itineraryData)
    return range?.label
  }, [offerId, itineraryData])

  const dateLabel = datesSummary ?? itineraryDayLabel
  const assistantHotelDetails = drawerHotel
    ? formatDrawerHotelInfo(drawerHotel)
    : undefined
  const showTitleInContent = shouldRenderTripDrawerTitleInContent(drawerPresentation)
  const drawerHeader = useMemo(() => {
    if (!drawerHotel || showTitleInContent) return undefined

    return (
      <TripDrawerHeaderTitle
        title={drawerHotel.name}
        subtitle={drawerHotel.location}
      />
    )
  }, [drawerHotel, showTitleInContent])

  const headerActions = useMemo(() => {
    if (!drawerHotel) return undefined
    const showMapLabel = formatTripDrawerMapLabel(intl, drawerPresentation)
    return (
      <Group direction="horizontal" gap={8} verticalAlign="center">
        {hasGeoLocation && onShowMap && (
          <FilterChip
            size="small"
            startIcon={<LineMapIcon />}
            onClick={handleShowMap}
          >
            {showMapLabel}
          </FilterChip>
        )}
        {isBooked && dateLabel && (
          <NonInteractiveChipWrapper>
            <FilterChip size="small">{dateLabel}</FilterChip>
          </NonInteractiveChipWrapper>
        )}
        {onSendMessage && !isBooked && (
          <AddHotelToTripButton
            itineraryData={itineraryData}
            hotelName={drawerHotel.name}
            offerId={drawerHotel.offerId}
            offerType={drawerHotel.offerType}
            hotelCity={drawerHotel.location}
            onSendMessage={onSendMessage}
            filterNights={filterNights}
            checkIn={liveCheckIn}
            checkOut={liveCheckOut}
            variant="outline"
            size="small"
          />
        )}
      </Group>
    )
  }, [
    drawerHotel,
    drawerPresentation,
    hasGeoLocation,
    handleShowMap,
    dateLabel,
    intl,
    onSendMessage,
    isBooked,
    itineraryData,
    filterNights,
    liveCheckIn,
    liveCheckOut,
    onShowMap,
  ])

  const failedToLoadLabel = intl.formatMessage({
    id: 'AyMdUB-fKPPyurSXTes2',
    defaultMessage: 'Failed to load hotel details.',
    description:
      'Error message shown when hotel offer details fail to load in the drawer',
  })

  const retryLabel = intl.formatMessage({
    id: 'q2nIV5-Sco41UZtxNQNg',
    defaultMessage: 'Try again',
    description:
      'Button label to retry loading hotel offer details after an error',
  })

  return (
    <TripDrawer
      isOpen={isOpen}
      onClose={onClose}
      onAfterClose={onAfterClose}
      onBack={onBack}
      animation={animation}
      direction="right-to-left"
      presentation={drawerPresentation}
      header={drawerHeader}
      headerActions={headerActions}
      footer={
        <TripDrawerAssistantFooter
          presentation={drawerPresentation}
          contextLabel={drawerHotel?.name}
          isOpen={isOpen}
          contextDetails={assistantHotelDetails}
        />
      }
    >
      <TripDrawerAssistantBridge
        presentation={drawerPresentation}
        isOpen={isOpen && !!drawerHotel}
        contextLabel={drawerHotel?.name}
        contextDetails={assistantHotelDetails}
      />
      {isLoading && (
        <DrawerDetailLoadingSkeleton />
      )}
      {isError && !isLoading && !drawerHotel && (
        <ErrorWrapper
          direction="vertical"
          gap={8}
          horizontalAlign="center"
          verticalAlign="center"
        >
          <BodyText variant="medium" colour="critical">
            {failedToLoadLabel}
          </BodyText>
          <TextButton kind="secondary" size="small" onClick={() => refetch()}>
            {retryLabel}
          </TextButton>
        </ErrorWrapper>
      )}
      {drawerHotel && (
        <HotelDetailView
          hotel={drawerHotel}
          onEnlargeMap={hasGeoLocation && onShowMap ? handleShowMap : undefined}
          titleInContent={showTitleInContent ? drawerHotel.name : undefined}
          showLocationInContent={showTitleInContent}
          reviews={reviews}
          reviewsLoading={reviewsLoading}
          isBooked={isBooked}
          orderId={orderIdForLink}
          savedBookedRooms={savedBookedRooms}
          itineraryData={itineraryData}
          onSendMessage={onSendMessage}
          checkIn={liveCheckIn}
          checkOut={liveCheckOut}
          onDatesChange={handleDatesChange}
          initialOccupancies={savedBooking?.occupancies ?? searchParams?.rooms}
          selectedPackageId={savedBooking?.packageId}
          selectedRoomTypeId={savedBooking?.roomTypeId}
          selectedRateId={savedBooking?.rateId}
          selectedCheckIn={savedBooking?.checkIn}
          selectedCheckOut={savedBooking?.checkOut}
          scrollToRoomOptions={scrollToRoomOptions}
          checkoutSource={checkoutSource}
        />
      )}
    </TripDrawer>
  )
}

export default ChatOfferDetailDrawer
